import { Controller } from '@hotwired/stimulus';
import AutoSave from 'stimulus/auto_save';

// Persists each field as it is filled in, so the Preview drawer and "Save draft" read the
// latest values. Listens on the step container: `change` bubbles, so one controller covers
// both the legacy simple_form markup and the kit fields replacing it.
export default class extends Controller {
  static targets = ['form'];

  static values = {
    url: String,
    debounce: { type: Number, default: 500 }
  };

  initialize() {
    // One per field name, so debouncing and aborting stay per field.
    this.savers = new Map();
  }

  disconnect() {
    this.savers.forEach(saver => saver.cancel());
    this.savers.clear();
  }

  save({ target }) {
    if (!this.urlValue || !this.saveable(target)) return;

    this.saverFor(target.name).save(this.valueOf(target));
  }

  saverFor(name) {
    if (!this.savers.has(name)) {
      this.savers.set(name, new AutoSave({
        url: this.urlValue,
        name,
        debounce: this.debounceValue,
        label: this.identifier
      }));
    }

    return this.savers.get(name);
  }

  saveable(field) {
    if (!field.name || field.disabled) return false;
    if (['file', 'submit', 'button', 'hidden'].includes(field.type)) return false;
    // Only the step form's fields belong to the job ad; other forms in the container post elsewhere.
    if (field.form !== this.formTarget) return false;

    return !this.ownsItsAutoSave(field);
  }

  // A field given its own `auto_save_url:` already PATCHes itself. The value is namespaced
  // per controller, which no attribute selector can match, hence the walk up.
  ownsItsAutoSave(field) {
    for (let node = field; node && node !== this.element; node = node.parentElement) {
      const owns = Array.from(node.attributes).some(
        ({ name, value }) => name.endsWith('-auto-save-url-value') && value !== ''
      );

      if (owns) return true;
    }

    return false;
  }

  valueOf(field) {
    if (field.type === 'checkbox') return this.checkboxValue(field);
    if (field.multiple) return Array.from(field.selectedOptions, option => option.value);

    return field.value;
  }

  // A group sends every checked box; a lone checkbox falls back to Rails' hidden unchecked
  // value, so clearing it persists as false rather than blank.
  checkboxValue(field) {
    const selector = `input[type=checkbox][name="${CSS.escape(field.name)}"]`;
    const group = this.element.querySelectorAll(selector);

    if (group.length > 1) {
      return Array.from(group).filter(box => box.checked).map(box => box.value);
    }

    if (field.checked) return field.value;

    const hidden = this.element.querySelector(`input[type=hidden][name="${CSS.escape(field.name)}"]`);

    return hidden ? hidden.value : '0';
  }
}
